import React, { useState } from "react";
import Select from "react-select";
import Swal from "sweetalert2";
import Table from "../../core/pagination/datatable";
import { getAuditLog } from "../../core/api/userService";
import { getApiErrorMessage } from "../../core/utils/apiError";

const today = () => new Date().toISOString().split("T")[0];
const firstOfMonth = () => { const d = new Date(); d.setDate(1); return d.toISOString().split("T")[0]; };

const ACTION_OPTIONS = [
  { value: "", label: "All Actions" },
  { value: "Login", label: "Login" },
  { value: "Logout", label: "Logout" },
  { value: "Created", label: "Created" },
  { value: "Updated", label: "Updated" },
  { value: "Deleted", label: "Deleted" },
  { value: "Viewed", label: "Viewed" },
];

const ENTITY_OPTIONS = [
  { value: "", label: "All Entities" },
  { value: "Patient", label: "Patient" },
  { value: "Appointment", label: "Appointment" },
  { value: "Invoice", label: "Invoice" },
  { value: "Purchase", label: "Purchase" },
  { value: "User", label: "User" },
  { value: "Auth", label: "Auth" },
];

const ACTION_BADGE = {
  Login: "success", Logout: "secondary", Created: "primary",
  Updated: "warning", Deleted: "danger", Viewed: "info",
};

const AuditLog = () => {
  const [filters, setFilters] = useState({ from: firstOfMonth(), to: today(), action: "", entity: "", userId: "" });
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);

  const runSearch = async () => {
    setLoading(true);
    try {
      const res = await getAuditLog(filters);
      setData(res.data?.records || res.data || []);
    } catch (err) {
      Swal.fire("Error", getApiErrorMessage(err, "Failed to load audit log."), "error");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Timestamp", dataIndex: "timestamp", width: 160,
      render: (v) => v ? new Date(v).toLocaleString("en-IN") : "—",
      sorter: (a, b) => new Date(a.timestamp) - new Date(b.timestamp),
    },
    { title: "User", dataIndex: "userName", width: 140 },
    {
      title: "Action", dataIndex: "action", width: 100,
      render: (v) => <span className={`badge bg-${ACTION_BADGE[v] || "secondary"}`}>{v}</span>,
    },
    { title: "Entity", dataIndex: "entityType", width: 110 },
    { title: "Entity Ref", dataIndex: "entityRef", width: 130, render: (v) => v || "—" },
    { title: "IP Address", dataIndex: "ipAddress", width: 130, render: (v) => v || "—" },
    {
      title: "Details", dataIndex: "details",
      render: (v, rec) => v ? (
        <div>
          <button className="btn btn-sm btn-link p-0 text-primary" onClick={() => setExpandedRow(expandedRow === rec.id ? null : rec.id)}>
            {expandedRow === rec.id ? "Hide" : "View"}
          </button>
          {expandedRow === rec.id && (
            <div className="p-2 bg-light rounded mt-1" style={{ maxWidth: 400 }}>
              <pre className="mb-0 small" style={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                {typeof v === "object" ? JSON.stringify(v, null, 2) : v}
              </pre>
            </div>
          )}
        </div>
      ) : "—",
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="page-title"><h4>Audit Log</h4><h6>Track all system activity and changes</h6></div>
        </div>

        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-2 align-items-end">
              <div className="col-auto">
                <label className="form-label small mb-1">From</label>
                <input type="date" className="form-control form-control-sm" value={filters.from}
                  onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))} />
              </div>
              <div className="col-auto">
                <label className="form-label small mb-1">To</label>
                <input type="date" className="form-control form-control-sm" value={filters.to}
                  onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))} />
              </div>
              <div className="col" style={{ minWidth: 150, maxWidth: 180 }}>
                <label className="form-label small mb-1">Action</label>
                <Select classNamePrefix="react-select" options={ACTION_OPTIONS} defaultValue={ACTION_OPTIONS[0]}
                  onChange={(opt) => setFilters((f) => ({ ...f, action: opt?.value || "" }))} />
              </div>
              <div className="col" style={{ minWidth: 150, maxWidth: 180 }}>
                <label className="form-label small mb-1">Entity</label>
                <Select classNamePrefix="react-select" options={ENTITY_OPTIONS} defaultValue={ENTITY_OPTIONS[0]}
                  onChange={(opt) => setFilters((f) => ({ ...f, entity: opt?.value || "" }))} />
              </div>
              <div className="col" style={{ minWidth: 160, maxWidth: 200 }}>
                <label className="form-label small mb-1">User (name/email)</label>
                <input className="form-control form-control-sm" value={filters.userId}
                  onChange={(e) => setFilters((f) => ({ ...f, userId: e.target.value }))}
                  placeholder="Filter by user" />
              </div>
              <div className="col-auto">
                <button className="btn btn-primary" onClick={runSearch} disabled={loading}>
                  {loading ? "Loading..." : "Search"}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-responsive">
              <Table
                columns={columns}
                dataSource={data}
                loading={loading}
              />
            </div>
            {data.length > 0 && (
              <div className="mt-2 text-muted small text-end">{data.length} records found</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditLog;
